/**
 * Shared action type helpers for consistent handling across components
 */

// Maximum number of interrogation rounds allowed per suspect
export const MAX_INTERROGATION_ROUNDS = 2;

/**
 * Check if an action is an interview/interrogation type (case-insensitive)
 * @param {string|null|undefined} action - The action type to check
 * @returns {boolean} - True if the action is an interview-type action
 */
export const isInterviewAction = (action) => {
  if (!action) return false;
  const normalized = action.toLowerCase();
  return normalized === 'interview' || normalized === 'interrogation' || normalized === 'interrogate';
};

/**
 * Get the normalized action type for display purposes
 * @param {string|null|undefined} action - The action type to normalize
 * @returns {string} - 'interrogation', 'interview', or the original action lowercased
 */
export const getNormalizedActionType = (action) => {
  if (!action) return '';
  const normalized = action.toLowerCase();
  if (normalized === 'interrogate' || normalized === 'interrogation') {
    return 'interrogation';
  }
  return normalized;
};

/**
 * Check if an action is a location-related action (case-insensitive)
 * @param {string|null|undefined} action - The action type to check
 * @returns {boolean} - True if the action is travel or examine
 */
export const isLocationAction = (action) => {
  if (!action) return false;
  const normalized = action.toLowerCase();
  return normalized === 'travel' || normalized === 'examine';
};

/**
 * Check if a suspect has been interviewed or interrogated
 * @param {string} suspectId - The suspect ID to check
 * @param {Object} interrogationDialogues - Map of suspect IDs to dialogue arrays
 * @param {Array} messageHistory - Array of message history objects
 * @returns {boolean} - True if the suspect has been interviewed/interrogated
 */
export const hasBeenInterviewed = (suspectId, interrogationDialogues, messageHistory) => {
  // Check interrogation dialogues first (preferred source)
  if (interrogationDialogues && interrogationDialogues[suspectId] && interrogationDialogues[suspectId].length > 0) {
    return true;
  }
  // Fall back to message history
  return (messageHistory || []).some(
    msg => isInterviewAction(msg.action) && msg.target === suspectId && msg.narrative
  );
};
